import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiClient, type ApiClientStatus } from './api-client';
import { API } from 'Amplify';

export type TermsDto = {
  terms: string;
  version: string;
};

/**
 * TermsApiClient is responsible for fetching the ToU via the terms API and
 * ensuring that only one API call is made per device, essentially debouncing multiple calls to it.
 *
 * It uses an observer pattern to notify registered listeners about the status of the API call.
 */
class TermsApiClient extends ApiClient<TermsDto> {
  // biome-ignore lint/complexity/noUselessConstructor: We need to keep the default constructor here, as the parent constructor is not public
  constructor() {
    super();
  }

  protected async preApiInvocationAction(): Promise<any> {
    return {};
  }

  protected async postApiInvocationAction(_: TermsDto): Promise<void> {}

  protected async apiInvocationErrorAction(error: any): Promise<void> {
    console.log(error);
  }

  protected getApiUri(): string {
    return '/terms';
  }
}

function useTerms() {
  const [terms, setTerms] = useState(null);
  const [_loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const fetchTerms = async () => {
    setLoading(true);
    try {
      const res = await API.get('IO_API', `/terms`, {});
      setTerms(res);
    } catch (e) {
      console.log(e);
      setError(`Could not load terms, please try again later - ${e}`);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTerms();
  }, []);

  if (error) {
    return { error };
  }

  return terms;
}

export { useTerms };

const termsApiClient = new TermsApiClient();

export type TermsApiHookType = {
  termsApiClientStatus: ApiClientStatus<TermsDto>;
  setUserName: (userName: string) => void;
};

export function useTermsApiHook(): TermsApiHookType {
  const [termsApiClientStatus, setTermsApiClientStatus] = useState(termsApiClient.getStatus());
  const userNameRef = useRef<string | null>(null);

  useEffect(() => {
    return termsApiClient.subscribe((newTermsApiClientStatus) => {
      setTermsApiClientStatus({ ...newTermsApiClientStatus });
    });
  }, []);

  const setUserName = useCallback((userName) => {
    if (userNameRef && userNameRef.current === userName) {
      return;
    }

    userNameRef.current = userName;

    termsApiClient.fetchData(userName).catch((e: any) => {
      console.error('Error fetching the Terms Of Use:', e);
    });
  }, []);

  return {
    termsApiClientStatus,
    setUserName,
  };
}
